import Fees from "./Components/fees/index.js";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate
} from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Home from "./Components/home/index.js";
import Login from "./Components/Login/login-page/Login.js";
import Signup from "./Components/sign-up/index.js";
import Contact from "./Components/Contact/index.js";
import HowWorks from "./Components/howWork/howWork-page/HowWorks.js";
import About from "./Components/about/index.js";
import Header from "./Components/header/Header/Header.js";
import UserAccount from "./Components/user-page/index.js";
import UserLayout from "./Components/layouts/UserLayout.jsx";
import AllBids from "./Components/user-page/user-pages/AllBids.js";
import Privacy from "./Components/privacy/index.js";
import Term from "./Components/term/index.js";
import Loan from "./Components/loanapplication/index.js";
import Profile from "./Components/user-page/user-pages/Profile.js";
import Offers from "./Components/user-page/user-pages/Offers.js";
import Order from "./Components/user-page/user-pages/Order.js";
import Verification from "./Components/sign-up/sign-page/Verification.js";
import VerifyEmail from "./Components/verifyEmail/index.js";
import ResetPassword from "./Components/Login/login-page/ResetPassword.js";
import ForgotPassword from "./Components/Login/login-page/ForgotPassword.js";
import UploadVehiclePage from "./Components/upload-vehicle/index.js";
import Funds from "./Components/user-page/user-pages/Funds.js";
import ProtectedRoute from "./utils/ProtectedRoutes.js";
import SearchPage from "./Components/SearchPage/index.js";
import VideoDemo from "./Components/video-demo/index.jsx";
import Vehicle from "./Components/vehicle/index.js";
import Review from "./Components/review/index.js";
import { useEffect, useState } from "react";
import { useAuthContext } from "./hooks/useAuthContext.js";
import Parts from "./Components/user-page/user-pages/Parts.js";
import SavedCars from "./Components/user-page/user-pages/SavedCars.js";
import ScrollToTop from "./utils/ScrollToTop.js";
import LocalVehicle from "./Components/localcars-details/index.js";
import LocalCarsSearchPage from "./Components/localCars-search/index.js";
import LocalCars from "./Components/user-page/user-pages/LocalCars.js";
import FooterSec from "./Components/Footer/index.js";
import LiveAuction from "./Components/live-auction/index.js";
import List from "./Components/live-aution-search/live-auction-search/List.js";
import Helpsec from "./Components/help/index.js";
import Invoices from "./Components/invoices/index.js";
import SavedSearchPage from "./Components/saved-searches/index.js";
import TransactionPage from "./Components/transaction-history/index.js"
import ProbiddingPage from "./Components/pro-bidding-tips/index.js"
import { isTokenExpired } from "./utils/isTokenExpired.js";
import { removeToken, removeUser, getToken } from "./utils/storageUtils.js";
import DealerInfo from "./Components/user-page/user-pages/DealerInfo.js"
import WireTranferList from "./Components/user-page/user-pages/wire-tranfer/WireTranferList.jsx";
import SignupPopup from "./Components/modals/SignupPopup.js";
import useSignupPopup from "./hooks/useSignupPopup.js";

function TokenMonitor() {
  const { dispatch } = useAuthContext();
  const navigate = useNavigate();

  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = getToken();
      if (token && isTokenExpired(token)) {
        // Token is expired, log out the user
        removeToken();
        removeUser();
        dispatch({ type: 'LOGOUT' });
        navigate('/login');
      }
    };

    // Check immediately on component mount
    checkTokenExpiration();

    // Set up interval to check regularly (every minute)
    const intervalId = setInterval(checkTokenExpiration, 10000);

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [dispatch, navigate]);

  return null; // This component doesn't render anything
}

function App() {
  
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const { user } = useAuthContext();
  const { showPopup, closePopup, handleSignupSuccess, handleSignupStart, forceShowPopup } = useSignupPopup();
  const [openType, setOpenType] = useState("");

  useEffect(() => {
    const existing = document.getElementById('leadconnector-widget-loader');
    if (existing) return;
    const script = document.createElement('script');
    script.id = 'leadconnector-widget-loader';
    script.src = 'https://widgets.leadconnectorhq.com/loader.js';
    script.async = true;
    script.setAttribute('data-resources-url', 'https://widgets.leadconnectorhq.com/chat-widget/loader.js');
    script.setAttribute('data-widget-id', '68bb9c42fa0aacb749b4f225');
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    const checkReviewPopup = () => {
      // Check if user is logged in
      if (user?.email) {
        const now = new Date().getTime();
        const loginTime = localStorage.getItem(`loginTime_${user.email}`);
        const hasReviewed = localStorage.getItem(`hasReviewed_${user.email}`);
        const lastPopupShow = localStorage.getItem(`lastPopupShow_${user.email}`);
  
        // If never reviewed and no popup shown in last 24 hours
        if (!hasReviewed && (!lastPopupShow || (now - parseInt(lastPopupShow)) > 86400000)) {
          if (!loginTime) {
            localStorage.setItem(`loginTime_${user.email}`, now.toString());
          } else {
            const elapsed = now - parseInt(loginTime);
            if (elapsed >= 7200000) { // 2 hours
              setShowReviewPopup(true);
              localStorage.setItem(`lastPopupShow_${user.email}`, now.toString());
            }
          }
        }
      } else {
        // Ensure the popup is not shown if there is no user
        setShowReviewPopup(false);
      }
    };
  
    checkReviewPopup();
  
    // Check every 5 minutes instead of every second
    const interval = setInterval(checkReviewPopup, 300000);
  
    return () => clearInterval(interval);
  }, [user]);

  // Listen for custom event to show signup popup from calculator
  useEffect(() => {
    const handleShowSignupPopup = () => {
      if (!user) {
        setOpenType("calculator_unlock");
        forceShowPopup();
      }
    };

    window.addEventListener('showSignupPopup', handleShowSignupPopup);
    
    return () => {
      window.removeEventListener('showSignupPopup', handleShowSignupPopup);
    };
  }, [user, forceShowPopup]);

  const handleClosePopup = () => {
    setOpenType("");
    if (user?.email) {
      setShowReviewPopup(false);
      localStorage.removeItem(`loginTime_${user.email}`);
    }
  };

  return (
    <HelmetProvider>
      <Router>
        <TokenMonitor />
        <div>
          {showReviewPopup && <Review user={user} onClose={handleClosePopup} />}
        </div>
        <SignupPopup 
          isOpen={showPopup} 
          onClose={() => { setOpenType(""); closePopup(); }} 
          onSignupSuccess={handleSignupSuccess}
          onSignupStart={handleSignupStart}
          openType={openType}
        />
        <div className="App">
          <ScrollToTop />
          <Header/>
          {/* Define your routes here */}
          <Routes>
            <Route path="/" element={<Home />} />
            <Route
              path="/signup"
              element={
                <ProtectedRoute>
                  <Signup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/login"
              element={
                <ProtectedRoute>
                  <Login />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                // <ProtectedRoute>
                <ResetPassword />
                // </ProtectedRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <ProtectedRoute>
                  <ForgotPassword />
                </ProtectedRoute>
              }
            />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route
              path="/user/documents-upload"
              element={
                <ProtectedRoute>
                  <Verification />
                </ProtectedRoute>
              }
            />

            <Route path="/contact-us" element={<Contact />} />
            <Route path="/how-it-works" element={<HowWorks />} />
            <Route path="/fees" element={<Fees />} />
            <Route path="/pro-bidding-tips" element={<ProtectedRoute><ProbiddingPage /></ProtectedRoute>} />
            <Route path="/help" element={<Helpsec />} />
            <Route path="/about-us" element={<About />} />
            <Route path="/privacy-policies" element={<Privacy />} />
            <Route path="/terms&conditions" element={<Term />} />

            <Route path="/apply-for-loan" element={
              <ProtectedRoute>
              <Loan />
              </ProtectedRoute>
              } />
            <Route
              path="/upload-local-car"
              element={
                <ProtectedRoute>
                  <UploadVehiclePage />
                </ProtectedRoute>
              }
            />
       
            <Route
              path="/live-auction-search"
              element={
                <ProtectedRoute>
                  <List/>
                </ProtectedRoute>
              }
            />
               <Route
              path="/live-auction-portal"
              element={
                <ProtectedRoute>
                  <LiveAuction/>
                </ProtectedRoute>
              }
            />


            <Route
              path="/user/get-all-invoices"
              element={
                <>
                  <ProtectedRoute>
                    <Invoices />
                  </ProtectedRoute>
                </>
              }
            />
            <Route
              path="/user/account"
              element={
                <>
                  <ProtectedRoute>
                    <UserAccount />
                  </ProtectedRoute>
                </>
              }
            />
            <Route
              path="/user/account/all-bids"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <AllBids />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/account/profile"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <Profile />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
             <Route
              path="/user/account/dealer-info"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <DealerInfo />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/account/offers"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <Offers />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/account/order"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <Order />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/account/order/local"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <Order />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/account/parts"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <Parts />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/account/funds"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <Funds />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
             <Route
              path="/user/account/transactions"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <TransactionPage />
                    </UserLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/account/wire-transfer-invoice-history"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <WireTranferList />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/user/account/saved-cars"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <SavedCars />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
             <Route
              path="/user/account/local-cars"
              element={
                <ProtectedRoute>
                  <UserLayout>
                    <LocalCars />
                  </UserLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/search-page" element={<SearchPage />} />
            <Route path="/saved-searches" element={ <ProtectedRoute><SavedSearchPage /></ProtectedRoute>} />
            <Route path="/video-demo" element={<VideoDemo />} />
            <Route path="/vehicle-detail/:lotID" element={<Vehicle />} />
            <Route path="/local-vehicle-detail/:id" element={<LocalVehicle />} />
            <Route
              path="/search-local-cars"
              element={<LocalCarsSearchPage />}
            />
            <Route path="/review" element={<Review />} />
            <Route path="/admin/dashboard" element={<>welcome</>} />
          </Routes>
          {/* Include the Footer component so it appears on all pages */}
          <FooterSec />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
